import { toNumber } from '@/pages/dashboard/Financial/utils/financialFormat';

export const REGISTRATION_VALIDATION_STATUS = Object.freeze({
  APPROVED: 'approved',
  BLOCKED: 'blocked',
  // Pré-cadastro ainda não concluído: quem roteia é a própria página do
  // Financeiro, que remonta o wizard. Não existe convite de validação aqui,
  // porque a CAF só pode começar depois que o pré-cadastro fica pronto.
  PRE_REGISTRATION: 'pre_registration',
  // Pré-cadastro pronto e validação cadastral (CAF) não iniciada: é o público do
  // convite exibido no clique do "Efetuar saque".
  NOT_STARTED: 'not_started',
  // Validação já enviada à CAF e em análise.
  PENDING: 'pending',
  // O suporte resetou a verificação e pediu que o seller refizesse o cadastro
  // (`User.reset_verification`). Não é reprovação: há caminho de volta, e é o
  // wizard de pré-cadastro. Separado de `REJECTED` porque a mensagem e o
  // destino são outros.
  RESUBMISSION_REQUESTED: 'resubmission_requested',
  REJECTED: 'rejected',
});

const REGISTRATION_STATUS_TO_VALIDATION_STATUS = Object.freeze({
  pending: REGISTRATION_VALIDATION_STATUS.PRE_REGISTRATION,
  pre_approved: REGISTRATION_VALIDATION_STATUS.NOT_STARTED,
  resubmission_requested: REGISTRATION_VALIDATION_STATUS.RESUBMISSION_REQUESTED,
  approved: REGISTRATION_VALIDATION_STATUS.APPROVED,
  gateway_pending: REGISTRATION_VALIDATION_STATUS.PENDING,
  gateway_pending_auto: REGISTRATION_VALIDATION_STATUS.PENDING,
  rejected: REGISTRATION_VALIDATION_STATUS.REJECTED,
  blocked: REGISTRATION_VALIDATION_STATUS.BLOCKED,
});

const DIGITAL_ACCOUNT_STATUSES = new Set([
  'pending',
  'pending_backgroundcheck',
  'approved_backgroundcheck',
  'reproved_backgroundcheck',
  'pending_documentscopy',
  'processing_documentscopy',
  'approved_documentscopy',
  'reproved_documentscopy',
  'approved_proposal',
  'reproved_proposal',
  'account_created',
  'account_error',
  'canceled',
  'cancelled',
]);

const normalizeStatus = (status) => (typeof status === 'string' ? status.trim().toLowerCase() : '');

/**
 * Fonte única do estágio do cadastro, para PF e PJ: `Company.status`.
 *
 * O ramo `type === 'individual' -> user.status` saiu porque o payload de
 * `GET /user/` não carrega `status`: PF devolvia `undefined` e todo o mecanismo
 * de modal e faixa ficava inerte justamente para quem mais precisa dele.
 * `Company.status` é o espelho sincronizado do status do split e é o único dos
 * dois que o cliente realmente recebe.
 */
export const getRegistrationStatus = ({ companyData } = {}) => companyData?.status ?? null;

export const isKnownDigitalAccountStatus = (accountStatus) =>
  DIGITAL_ACCOUNT_STATUSES.has(normalizeStatus(accountStatus));

export const isApprovedDigitalAccountStatus = (accountStatus) =>
  normalizeStatus(accountStatus) === 'account_created';

export const getRegistrationValidationStatusKey = ({ registrationStatus, accountStatus } = {}) => {
  const normalizedRegistrationStatus = normalizeStatus(registrationStatus);

  if (!REGISTRATION_STATUS_TO_VALIDATION_STATUS[normalizedRegistrationStatus]) return null;

  return `${normalizedRegistrationStatus}:${normalizeStatus(accountStatus) || 'no-account'}`;
};

export const resolveRegistrationValidationStatus = ({ registrationStatus, accountStatus } = {}) => {
  if (isApprovedDigitalAccountStatus(accountStatus)) return null;

  return REGISTRATION_STATUS_TO_VALIDATION_STATUS[normalizeStatus(registrationStatus)] || null;
};

export const hasSaleForRegistrationValidation = ({ totalSales, releaseAmount } = {}) =>
  toNumber(totalSales) > 0 || toNumber(releaseAmount) > 0;
